import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, ExternalLink, MonitorPlay, Power } from 'lucide-react';

import { ApiError } from '@/lib/api/errors';
import {
  xurveyApi,
  xurveyControlUrl,
  xurveyScreenUrl,
  type ScreenControl,
} from '@/lib/api/xurvey';
import { useT } from '@/lib/i18n/useT';
import { toast } from '@/components/ui/Toast';

/**
 * Las pantallas del evento y el mando de quien está en el sitio.
 *
 * Dos direcciones que salen de aquí y no vuelven al sistema:
 *
 *  · **La pantalla** se pega en el navegador de la tele del recinto. Enseña el
 *    cartel con su QR y, mientras el evento no esté en marcha, lo difumina con
 *    un «empieza pronto».
 *  · **El mando** se le manda al agente con su código. Enciende y apaga el
 *    evento entero —todas sus estaciones— sin entrar en Xotski.
 *
 * El código va aparte del enlace a propósito: la URL de la pantalla acaba en un
 * navegador colgado de una pared, a la vista de cualquiera que pase.
 */
export function ScreenControlCard({
  nombreEvento,
  linkIds,
}: {
  nombreEvento: string;
  /** Los enlaces de este evento: uno por estación desplegada. */
  linkIds: number[];
}) {
  const t = useT();
  const [mando, setMando] = useState<ScreenControl | null>(null);

  const crear = useMutation({
    mutationFn: () =>
      xurveyApi.ensureScreen({ name: nombreEvento, linkIds }),
    onSuccess: (d) => setMando(d),
    onError: (e: unknown) =>
      toast({
        tone: 'error',
        title: e instanceof ApiError ? e.message : t('designJourney.screenFailed'),
      }),
  });

  const copiar = (texto: string) => {
    void navigator.clipboard.writeText(texto);
    toast({ tone: 'success', title: t('common.copied') });
  };

  if (linkIds.length === 0) {
    return (
      <p className="text-[11px] leading-snug text-[var(--color-fg-muted)]">
        {t('designJourney.screenNeedsDeploy')}
      </p>
    );
  }

  if (!mando) {
    return (
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => crear.mutate()}
          disabled={crear.isPending}
          className="flex items-center justify-center gap-2 rounded-lg border border-[var(--color-border-default)] px-3 py-2 text-[12px] font-semibold hover:border-[var(--color-accent)] disabled:opacity-50"
        >
          <MonitorPlay className="h-4 w-4" />
          {crear.isPending ? t('common.loading') : t('designJourney.screenGet')}
        </button>
        <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
          {t('designJourney.screenHint', { n: String(linkIds.length) })}
        </p>
      </div>
    );
  }

  const urlPantalla = xurveyScreenUrl(mando.token);
  const urlMando = xurveyControlUrl(mando.token);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span
          className={
            mando.isOnline
              ? 'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--color-success-fg,#16a34a)]'
              : 'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--color-fg-muted)]'
          }
        >
          {mando.isOnline ? t('designJourney.screenOn') : t('designJourney.screenOff')}
        </span>
        <span className="text-[10px] text-[var(--color-fg-muted)]">
          {t('designJourney.screenHint', { n: String(mando.linkIds?.length ?? 0) })}
        </span>
      </div>

      <Fila
        titulo={t('designJourney.screenUrl')}
        nota={t('designJourney.screenUrlHint')}
        url={urlPantalla}
        onCopiar={() => copiar(urlPantalla)}
      />

      <Fila
        titulo={t('designJourney.controlUrl')}
        nota={t('designJourney.controlUrlHint')}
        url={urlMando}
        onCopiar={() => copiar(urlMando)}
      />

      {/*
        El código, grande y aparte.

        Es lo único que hay que dictar por teléfono o escribir en una mano, y va
        separado del enlace porque quien ve la pantalla no debe poder apagarla.
      */}
      <div className="flex items-center gap-3 rounded-lg border border-[var(--color-border-default)] p-2.5">
        <Power className="h-4 w-4 flex-none text-[var(--color-fg-muted)]" />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wide text-[var(--color-fg-muted)]">
            {t('designJourney.controlCode')}
          </div>
          <div className="font-mono text-[22px] tracking-[0.3em]">{mando.code}</div>
        </div>
        <button
          type="button"
          onClick={() => copiar(mando.code)}
          aria-label={t('common.copy')}
          className="rounded-md border border-[var(--color-border-default)] p-1.5 hover:border-[var(--color-accent)]"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>

      <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
        {t('designJourney.controlWarning')}
      </p>
    </div>
  );
}

/** Una dirección con su QR: se abre en la tele escaneándolo desde el móvil. */
function Fila({
  titulo,
  nota,
  url,
  onCopiar,
}: {
  titulo: string;
  nota: string;
  url: string;
  onCopiar: () => void;
}) {
  const t = useT();
  return (
    <div className="flex items-start gap-3 rounded-lg border border-[var(--color-border-default)] p-2.5">
      <div className="rounded bg-white p-1">
        <QRCodeSVG value={url} size={64} level="M" marginSize={0} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[12px] font-semibold">{titulo}</span>
        <span className="text-[10px] leading-snug text-[var(--color-fg-muted)]">{nota}</span>
        <code className="block truncate font-mono text-[10px] text-[var(--color-fg-muted)]">
          {url}
        </code>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={onCopiar}
            className="rounded-md border border-[var(--color-border-default)] px-2 py-0.5 text-[11px] hover:border-[var(--color-accent)]"
          >
            {t('common.copy')}
          </button>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 rounded-md border border-[var(--color-border-default)] px-2 py-0.5 text-[11px] hover:border-[var(--color-accent)]"
          >
            <ExternalLink className="h-3 w-3" />
            {t('common.open')}
          </a>
        </div>
      </div>
    </div>
  );
}
